import React, { useState } from 'react';
import { X, Bell, Mail, MessageSquare } from 'lucide-react';
import { authAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

export default function SettingsModal({ onClose }) {
  const { user, updateUser } = useAuth();
  const [form, setForm] = useState({
    phone: user.phone || '',
    email_enabled: user.email_enabled !== false,
    whatsapp_enabled: !!user.whatsapp_enabled,
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await authAPI.updateSettings(form);
      updateUser(res.data);
      toast.success('Settings saved! 🔔');
      onClose();
    } catch (err) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={styles.overlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={styles.modal}>
        <div style={styles.header}>
          <div style={styles.headerLeft}>
            <Bell size={22} color="#6366f1" />
            <h2 style={styles.title}>Notification Settings</h2>
          </div>
          <button onClick={onClose} style={styles.closeBtn}><X size={20} /></button>
        </div>

        <div style={styles.body}>
          {/* Profile */}
          <div style={styles.profileCard}>
            {user.picture && <img src={user.picture} alt="" style={styles.avatar} />}
            <div>
              <div style={styles.profileName}>{user.name}</div>
              <div style={styles.profileEmail}>{user.email}</div>
            </div>
          </div>

          {/* Email notifications */}
          <div style={styles.section}>
            <div style={styles.sectionHeader}>
              <Mail size={18} color="#6366f1" />
              <span style={styles.sectionTitle}>Email Notifications</span>
              <label style={styles.toggle}>
                <input
                  type="checkbox"
                  checked={form.email_enabled}
                  onChange={(e) => setForm(f => ({ ...f, email_enabled: e.target.checked }))}
                  style={{ display: 'none' }}
                />
                <div style={{ ...styles.toggleTrack, background: form.email_enabled ? '#6366f1' : '#ddd' }}>
                  <div style={{ ...styles.toggleThumb, transform: form.email_enabled ? 'translateX(22px)' : 'translateX(2px)' }} />
                </div>
              </label>
            </div>
            <p style={styles.sectionDesc}>
              Receive an email reminder the day before any subscription expires.
              Sent to: <strong>{user.email}</strong>
            </p>
          </div>

          {/* WhatsApp notifications */}
          <div style={styles.section}>
            <div style={styles.sectionHeader}>
              <MessageSquare size={18} color="#25D366" />
              <span style={styles.sectionTitle}>WhatsApp Notifications</span>
              <label style={styles.toggle}>
                <input
                  type="checkbox"
                  checked={form.whatsapp_enabled}
                  onChange={(e) => setForm(f => ({ ...f, whatsapp_enabled: e.target.checked }))}
                  style={{ display: 'none' }}
                />
                <div style={{ ...styles.toggleTrack, background: form.whatsapp_enabled ? '#25D366' : '#ddd' }}>
                  <div style={{ ...styles.toggleThumb, transform: form.whatsapp_enabled ? 'translateX(22px)' : 'translateX(2px)' }} />
                </div>
              </label>
            </div>

            <div style={styles.phoneField}>
              <label style={styles.label}>Your WhatsApp Number</label>
              <input
                style={styles.input}
                placeholder="+44 7700 900000"
                value={form.phone}
                onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))}
              />
              <p style={styles.hint}>Include country code, e.g. +44 for UK</p>
            </div>

            {form.whatsapp_enabled && !form.phone && (
              <div style={styles.warning}>
                ⚠️ Add your phone number above to receive WhatsApp messages
              </div>
            )}
          </div>

          {/* Info */}
          <div style={styles.infoBox}>
            <span style={{ fontSize: '18px' }}>💡</span>
            <div style={{ fontSize: '13px', color: '#666', lineHeight: '1.5' }}>
              Notifications are sent at <strong>9:00 AM</strong> the day before expiry.
              WhatsApp uses Twilio sandbox — you may need to opt-in by messaging <code>join [sandbox-name]</code> to the Twilio number first.
            </div>
          </div>
        </div>

        <div style={styles.footer}>
          <button onClick={onClose} style={styles.cancelBtn}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={styles.saveBtn}>
            {saving ? '⏳ Saving...' : '💾 Save Settings'}
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed', inset: 0,
    background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, padding: '20px',
  },
  modal: {
    background: 'white', borderRadius: '24px',
    width: '100%', maxWidth: '480px',
    boxShadow: '0 32px 64px rgba(0,0,0,0.3)',
    overflow: 'hidden',
  },
  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '24px 28px',
    borderBottom: '1px solid #f0f0f0',
  },
  headerLeft: { display: 'flex', alignItems: 'center', gap: '10px' },
  title: { margin: 0, fontSize: '18px', fontWeight: '700', color: '#1a1a2e' },
  closeBtn: {
    background: '#f5f5f5', border: 'none', borderRadius: '50%',
    width: '36px', height: '36px',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer', color: '#666',
  },
  body: { padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: '20px' },
  profileCard: {
    display: 'flex', alignItems: 'center', gap: '14px',
    background: 'linear-gradient(135deg, #f0f0ff, #f0f9ff)',
    borderRadius: '14px', padding: '16px',
  },
  avatar: { width: '48px', height: '48px', borderRadius: '50%' },
  profileName: { fontWeight: '700', color: '#1a1a2e', fontSize: '15px' },
  profileEmail: { color: '#888', fontSize: '13px', marginTop: '2px' },
  section: {
    border: '2px solid #f0f0f0', borderRadius: '16px', padding: '16px',
  },
  sectionHeader: {
    display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px',
  },
  sectionTitle: { fontWeight: '700', color: '#1a1a2e', fontSize: '15px', flex: 1 },
  toggle: { cursor: 'pointer' },
  toggleTrack: {
    width: '46px', height: '26px', borderRadius: '13px',
    position: 'relative', transition: 'background 0.2s',
  },
  toggleThumb: {
    position: 'absolute', top: '3px',
    width: '20px', height: '20px', background: 'white',
    borderRadius: '50%', transition: 'transform 0.2s',
    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
  },
  sectionDesc: { margin: 0, fontSize: '13px', color: '#888', lineHeight: '1.5' },
  phoneField: { marginTop: '12px' },
  label: { display: 'block', fontSize: '12px', fontWeight: '600', color: '#555', marginBottom: '6px' },
  input: {
    width: '100%', padding: '10px 14px',
    border: '2px solid #e8e8e8', borderRadius: '10px',
    fontSize: '14px', outline: 'none', boxSizing: 'border-box',
  },
  hint: { margin: '4px 0 0', fontSize: '11px', color: '#aaa' },
  warning: {
    marginTop: '10px', padding: '10px 14px',
    background: '#fffbeb', border: '1px solid #fde68a',
    borderRadius: '10px', fontSize: '13px', color: '#92400e',
  },
  infoBox: {
    display: 'flex', gap: '10px',
    background: '#f8f9ff', borderRadius: '12px', padding: '14px',
  },
  footer: {
    display: 'flex', gap: '12px',
    padding: '16px 28px 24px',
    borderTop: '1px solid #f0f0f0',
  },
  cancelBtn: {
    flex: 1, padding: '12px',
    border: '2px solid #e8e8e8', borderRadius: '12px',
    background: 'white', color: '#666', fontSize: '14px', fontWeight: '600', cursor: 'pointer',
  },
  saveBtn: {
    flex: 2, padding: '12px',
    border: 'none', borderRadius: '12px',
    background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
    color: 'white', fontSize: '14px', fontWeight: '700', cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(99,102,241,0.3)',
  },
};
